import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { WorkflowRuleFullPage } from "@/components/Organisms/WorkflowRuleFullPage/WorkflowRuleFullPage";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import { getJobTypeDisplayName } from "@/utils/job-type-detail/job-type-detail.utils";
import { JobTypeDrawerContext } from "@/utils/job-type-detail/job-type-drawer-context";

const SearchSchema = z.object({
	ruleTab: z.enum(["details", "activity"]).optional().default("details"),
});

const ROUTE_ID =
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full/rules/$ruleId" as const;

export const Route = createFileRoute(ROUTE_ID)({
	component: OrgJobTypeRuleDetailPage,
	validateSearch: (search) => SearchSchema.parse(search),
	params: {
		parse: (raw) => {
			const p = raw as { orgId: string; jobTypeId: string; ruleId: string };
			return {
				orgId: z.string().parse(p.orgId),
				jobTypeId: z.string().parse(p.jobTypeId),
				ruleId: z.string().parse(p.ruleId),
			};
		},
	},
});

function OrgJobTypeRuleDetailPage() {
	const ctx = useContext(JobTypeDrawerContext);
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({ from: ROUTE_ID });
	const search = useSearch({ from: ROUTE_ID });

	const canEditRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "EditWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canEditRule = Boolean(canEditRuleQuery.data);

	const canDeleteRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "DeleteWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canDeleteRule = Boolean(canDeleteRuleQuery.data);

	const canArchiveRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ArchiveWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canArchiveRule = Boolean(canArchiveRuleQuery.data);

	if (!ctx) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);
	}

	const { jobType } = ctx;

	return (
		<WorkflowRuleFullPage
			ruleId={params.ruleId}
			mode="organization"
			organizationId={params.orgId}
			lifecycleId={jobType.lifecycleId}
			entityTypeId={params.jobTypeId}
			canEdit={canEditRule}
			canDelete={canDeleteRule}
			canArchive={canArchiveRule}
			entityTypeLabel={t("admin.jobTypes.table.title")}
			entityTypeName={getJobTypeDisplayName(jobType)}
			activeTab={search.ruleTab}
			onTabChange={(ruleTab) => {
				void navigate({
					to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full/rules/$ruleId",
					params: {
						orgId: params.orgId,
						jobTypeId: params.jobTypeId,
						ruleId: params.ruleId,
					},
					search: { tab: "rules", mode: "view", presentation: "full", ruleTab },
				});
			}}
			onBackToRules={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
					params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onNavigateToEntityTypeList={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/jobTypes",
					params: { orgId: params.orgId },
				});
			}}
			onNavigateToEntityTypeRules={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
					params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onAfterAction={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
					params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
		/>
	);
}
